import {Action, Button, Card, DataStatus} from 'platform/components';
import {Box, HStack, Right, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {useGetRentalOrderQuery} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {Main} from '@dms/shared';

import {buildArray, composePath, TestIdProps, useBoolean, useNavigate} from 'shared/web';

import {CustomerCard} from './components/CustomerCard';
import {RentalVehicleDetail} from './components/RentalVehicleDetail';
import {RentalVehicleSelect} from './components/RentalVehicleSelect';

interface VehicleAndCustomerProps extends TestIdProps {
  rentalOrderId: string | undefined;
  setRentalOrderId: (rentalOrderId: string | undefined) => void;
}

export function VehicleAndCustomer(props: VehicleAndCustomerProps) {
  const navigate = useNavigate();

  const {
    data: rentalOrder,
    isLoading: isLoadingRentalOrder,
    isError: isErrorRentalOrder,
  } = useGetRentalOrderQuery(
    {rentalOrderId: props.rentalOrderId ?? ''},
    {skip: !props.rentalOrderId}
  );

  const [isVehicleEditModeOpened, openVehicleEditMode, closeVehicleEditMode] = useBoolean(
    isNil(props.rentalOrderId)
  );

  const handleReserve = () => {
    if (isNil(props.rentalOrderId)) {
      throw new Error('Rental order ID is required');
    }

    navigate(composePath(rentalOrderRoutes.rentVehicle, {params: {id: props.rentalOrderId}}));
  };

  const vehicleCardActions: Action[] = buildArray<Action>()
    .when(isNotNil(rentalOrder?.rentalVehicleId), {
      type: 'button',
      variant: 'link',
      leftIcon: 'action/open_in_new',
      title: i18n.t('entity.vehicle.labels.vehicleDetails'),
      'data-testid': testIds.rentalOrder.vehicleAndCustomer('vehicleDetails'),
      to: rentalOrder?.rentalVehicleId
        ? composePath(vehiclesRoutes.detail, {params: {id: rentalOrder.rentalVehicleId}})
        : undefined,
      target: '_blank',
    })
    .when(isNotNil(rentalOrder?.rentalVehicleId) && !isVehicleEditModeOpened, {
      type: 'button',
      variant: 'link',
      leftIcon: 'navigation/refresh',
      title: i18n.t('page.rentalOrder.labels.changeVehicleOrDate'),
      'data-testid': testIds.rentalOrder.vehicleAndCustomer('changeVehicleOrDate'),
      onClick: openVehicleEditMode,
    })
    .when(isNotNil(rentalOrder?.rentalVehicleId) && isVehicleEditModeOpened, {
      type: 'button',
      variant: 'dangerLink',
      leftIcon: 'navigation/cancel',
      title: i18n.t('general.actions.discardChanges'),
      'data-testid': testIds.rentalOrder.vehicleAndCustomer('discardChanges'),
      onClick: closeVehicleEditMode,
    });

  const rentalVehicleComponent = match([
    isNotNil(rentalOrder?.rentalVehicleId),
    isVehicleEditModeOpened,
    isNotNil(props.rentalOrderId),
  ])
    .with([Pattern.any, Pattern.any, false], () => (
      <RentalVehicleSelect onAfterSubmit={(id) => props.setRentalOrderId(id)} />
    ))
    .with([false, Pattern.any, true], () => (
      <RentalVehicleSelect rentalOrder={rentalOrder} onAfterSubmit={closeVehicleEditMode} />
    ))
    .with([true, true, true], () => (
      <RentalVehicleSelect rentalOrder={rentalOrder} onAfterSubmit={closeVehicleEditMode} />
    ))
    .otherwise(() => <RentalVehicleDetail rentalOrder={rentalOrder} />);

  return (
    <Main isFullHeight>
      <VStack spacing={4}>
        <HStack spacing={4}>
          <Box flex={1}>
            <Card
              title={i18n.t('page.rentalOrder.labels.rentalVehicle')}
              data-testid={testIds.rentalOrder.vehicleAndCustomer('rentalVehicle')}
              actions={vehicleCardActions}
            >
              <DataStatus
                isLoading={isLoadingRentalOrder}
                isError={isErrorRentalOrder}
                minHeight={isNil(rentalOrder?.rentalVehicleId) ? 61 : 160}
              >
                {rentalVehicleComponent}
              </DataStatus>
            </Card>
          </Box>

          <Box flex={1}>
            <CustomerCard
              rentalOrder={rentalOrder}
              setRentalOrderId={props.setRentalOrderId}
              data-testid={testIds.rentalOrder.vehicleAndCustomer('customerCard')}
            />
          </Box>
        </HStack>
        <Right>
          <Button
            variant="outlined"
            title={i18n.t('general.actions.continue')}
            onClick={handleReserve}
            isDisabled={isNil(rentalOrder?.rentalVehicleId) || isNil(rentalOrder?.customerId)}
          />
        </Right>
      </VStack>
    </Main>
  );
}
